import PlaySVG from '@public/icons/play.svg';
import { MotionValue, m as motion, useTransform } from 'framer-motion';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { clamp } from '@/shared/lib/utils/clamp';
import { lerp } from '@/shared/lib/utils/lerp';
import Lazy from '../../shared/Lazy';

type Props = {
    shortVideo: string;
    longVideo?: string;
    scrollYProgress: MotionValue<number>;
    phase1End: number;
    phase2End: number;
    phase3End: number;
};

const getCircleInsets = (width: number, height: number, diameter: number) => {
    const insetH = Math.max(0, ((width - diameter) / (2 * width)) * 100);
    const insetV = Math.max(0, ((height - diameter) / (2 * height)) * 100);

    return { insetH, insetV };
};

const buildInsetClip = (insetV: number, insetH: number, round: number) =>
    `inset(${insetV}% ${insetH}% ${insetV}% ${insetH}% round ${round}px)`;

type HTMLVideoElementWithWebkit = HTMLVideoElement & {
    webkitEnterFullscreen?: () => void;
};

function LiveVideoClipSection({ shortVideo, longVideo, scrollYProgress, phase1End, phase2End, phase3End }: Props) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const sizeRef = useRef({ width: 1920, height: 1080 });
    const shortVideoRef = useRef<HTMLVideoElement>(null);
    const longVideoRef = useRef<HTMLVideoElement>(null);

    const CIRCLE_RADIUS = 175;
    const CIRCLE_DIAMETER = CIRCLE_RADIUS * 2;
    const RECT_INSET_H = 24;
    const RECT_INSET_V = 24;
    const RECT_ROUND = 100;
    const travelStartY = 400;

    useLayoutEffect(() => {
        const el = wrapperRef.current;

        if (!el) {
            return;
        }

        const updateSize = () => {
            const { width, height } = el.getBoundingClientRect();

            if (width > 0 && height > 0) {
                sizeRef.current = { width, height };
            }
        };

        updateSize();

        const resizeObserver = new ResizeObserver(updateSize);

        resizeObserver.observe(el);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const videoOpacity = useTransform(scrollYProgress, [0.01, phase1End * 0.5], [0, 1]);
    const yPos = useTransform(scrollYProgress, [0.01, phase2End], [travelStartY, 0]);
    const playOpacity = useTransform(scrollYProgress, [0.94, 0.97], [0, 1]);
    const playPointerEvents = useTransform(playOpacity, (value) => (value > 0 ? 'auto' : 'none'));

    const clipPath = useTransform(scrollYProgress, (progress) => {
        const { width, height } = sizeRef.current;
        const { insetH: circleInsetH, insetV: circleInsetV } = getCircleInsets(width, height, CIRCLE_DIAMETER);

        if (progress <= phase3End) {
            const t = progress <= phase2End ? 0 : clamp((progress - phase2End) / (phase3End - phase2End), 0, 1);
            const insetH = lerp(circleInsetH, RECT_INSET_H, t);
            const insetV = lerp(circleInsetV, RECT_INSET_V, t);
            const round = lerp(CIRCLE_RADIUS, RECT_ROUND, t);

            return buildInsetClip(insetV, insetH, round);
        }

        const t = clamp((progress - phase3End) / (1 - phase3End), 0, 1);
        const insetH = lerp(RECT_INSET_H, 0, t);
        const insetV = lerp(RECT_INSET_V, 0, t);
        const round = lerp(RECT_ROUND, 0, t);

        return buildInsetClip(insetV, insetH, round);
    });

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        shortVideoRef.current?.play();
                    } else {
                        shortVideoRef.current?.pause();
                    }
                });
            },
            { rootMargin: '0% 0% 0% 0%' },
        );

        if (wrapperRef.current) {
            observer.observe(wrapperRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const handleFullscreenChange = () => {
            if (document.fullscreenElement !== longVideoRef.current) {
                longVideoRef.current?.pause();
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    const handlePlayClick = () => {
        if (longVideoRef.current) {
            const video = longVideoRef.current;
            if (video.requestFullscreen) {
                video.requestFullscreen();
            } else {
                (video as HTMLVideoElementWithWebkit).webkitEnterFullscreen?.();
            }
            video.play();
            window._txq?.push(['track', 'Play_video']);
        }
    };

    return (
        <motion.div
            ref={wrapperRef}
            className="live-video-clip-section"
            style={{
                clipPath,
                y: yPos,
                opacity: videoOpacity,
            }}
        >
            <motion.button
                type="button"
                className="video-play-btn"
                aria-label="Воспроизвести видео"
                style={{ opacity: playOpacity, pointerEvents: playPointerEvents }}
                onClick={handlePlayClick}
            >
                <PlaySVG width={60} height={60} />
            </motion.button>
            <Lazy ref={shortVideoRef} offset={100}>
                <video className="video-clip-short" autoPlay muted playsInline loop data-src={shortVideo}></video>
            </Lazy>

            <Lazy ref={longVideoRef} offset={100}>
                <video className="video-clip-long" data-src={longVideo}></video>
            </Lazy>
        </motion.div>
    );
}

export default LiveVideoClipSection;
